import React from "react";
import { Link } from "react-router-dom";
import { CarIcon, SparklesIcon, CreditCardIcon, PercentIcon, RepeatIcon, BriefcaseIcon, HeadphonesIcon, ArrowRightIcon, MessageCircleIcon, BoxIcon } from "lucide-react";
import { Section, SectionHeading } from "../ui/Section";
import { Reveal } from "../ui/Reveal";
import { whatsappLink } from "../../utils/vehicles";
import { trackPixel } from "../../utils/pixel";
interface Servicio {
  icon: BoxIcon;
  title: string;
  text: string;
  cta: string;
  /** Ruta interna o, si es WhatsApp, el link externo. */
  to?: string;
  href?: string;
}
const GESTION_WA = 'Hola VCARS, quiero que gestionen la venta de mi auto. ¿Cómo es el proceso?';
const ASESOR_WA = 'Hola VCARS, quiero asesoramiento personalizado para elegir mi próximo auto.';
const servicios: Servicio[] = [{
  icon: CarIcon,
  title: 'Venta de usados seleccionados',
  text: 'Unidades revisadas, con historial y documentación al día.',
  cta: 'Ver catálogo',
  to: '/catalogo'
}, {
  icon: SparklesIcon,
  title: 'Venta de vehículos 0KM',
  text: 'Elegí marca y modelo, coordinamos la entrega.',
  cta: 'Ver unidades 0KM',
  to: '/catalogo?estado=0KM'
}, {
  icon: CreditCardIcon,
  title: 'Financiación hasta el 100%',
  text: 'Simulá tu crédito: evaluamos anticipo, permuta y perfil bancario.',
  cta: 'Simular mi crédito',
  to: '/simulacion-credito'
}, {
  icon: PercentIcon,
  title: 'Tasa 0% en opciones disponibles',
  text: 'Consultá qué unidades tienen tasa promocional.',
  cta: 'Consultar financiación',
  to: '/financiacion'
}, {
  icon: RepeatIcon,
  title: 'Aceptamos permutas',
  text: 'Cargá los datos de tu auto y el monto que pretendés por él.',
  cta: 'Cotizar mi permuta',
  to: '/permutas'
}, {
  icon: BriefcaseIcon,
  title: 'Gestión de ventas particulares',
  text: 'Publicamos, filtramos interesados y cerramos la venta por vos.',
  cta: 'Hablar por WhatsApp',
  href: whatsappLink(GESTION_WA)
}, {
  icon: HeadphonesIcon,
  title: 'Asesoramiento personalizado',
  text: 'Atención clara y cercana en cada paso.',
  cta: 'Hablar por WhatsApp',
  href: whatsappLink(ASESOR_WA)
}];
const cardClass = 'group flex h-full flex-col rounded-2xl border border-line bg-surface2/40 p-6 backdrop-blur-sm transition-[border-color,transform,box-shadow] duration-200 ease-smooth hover:-translate-y-0.5 hover:border-neon/55 hover:shadow-[0_0_32px_-14px_rgba(31,123,255,0.95)] focus-visible:border-neon focus-visible:outline-none';
function CardBody({
  icon: Icon,
  title,
  text,
  cta,
  isWhatsapp
}: Servicio & {
  isWhatsapp: boolean;
}) {
  return <>
      <span className="inline-grid h-11 w-11 place-items-center rounded-xl bg-neon/12 text-neon transition-colors duration-150 ease-smooth group-hover:bg-neon/20" data-id="element-497" data-mp="30dsiztnsr1lj">
        <Icon className="h-5 w-5" aria-hidden="true" data-id="element-498" data-mp="35d5346na9ukf" />
      </span>
      <h3 className="mt-5 text-[17px] font-bold leading-snug text-white" data-id="element-499" data-mp="2qfcrhpkuxqbi">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-mist" data-id="element-500" data-mp="2vf8rldqfoxls">{text}</p>
      <span className={`mt-auto inline-flex items-center gap-1.5 pt-5 text-[13.5px] font-bold ${isWhatsapp ? 'text-[#25c264]' : 'text-neon-soft'}`} data-id="element-501" data-mp="2vgjw5frpe9dy">
        {isWhatsapp ? <MessageCircleIcon className="h-4 w-4" aria-hidden="true" data-id="element-502" data-mp="27swtexuu7sq1" /> : null}
        {cta}
        <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-smooth group-hover:translate-x-0.5" aria-hidden="true" data-id="element-503" data-mp="2lrwig6m9i9mq" />
      </span>
    </>;
}
export function Servicios() {
  return <Section id="servicios" data-id="element-504" data-mp="2uqqd3rg18pxn">
      <Reveal data-id="element-505" data-mp="2upymyw57fker">
        <SectionHeading title="Nuestros servicios" lead="En VCARS no solo vendemos autos. También acompañamos a quienes quieren vender su vehículo: nos encargamos de la gestión comercial para concretar la operación de manera segura, ordenada y profesional." data-id="element-506" data-mp="2upf7dm31183w" />
      </Reveal>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-507" data-mp="2zj9yvbovirdj">
        {servicios.map((servicio, i) => <Reveal key={servicio.title} delay={Math.min(i, 5) * 0.04} className="h-full" data-id="element-508" data-mp="3eg0powkheurj" data-mpi={i}>
            {servicio.href ? <a href={servicio.href} target="_blank" rel="noopener noreferrer" onClick={() => trackPixel('Contact')} className={cardClass} data-id="element-509" data-mp="39iimlmt5b9rg">
                <CardBody {...servicio} isWhatsapp data-id="element-510" data-mp="1bslmq5leqdov" />
              </a> : <Link to={servicio.to ?? '/'} className={cardClass} data-id="element-511" data-mp="1br0s6fz3eur0">
                <CardBody {...servicio} isWhatsapp={false} data-id="element-512" data-mp="1gl50p3o0bau7" />
              </Link>}
          </Reveal>)}
      </div>

      <Reveal data-id="element-513" data-mp="1lidkf8oszmxk">
        <p className="mt-8 max-w-4xl text-[15px] leading-relaxed text-mist" data-id="element-514" data-mp="1li5a9ok2evq2">
          También aceptamos permutas, ofrecemos alternativas de financiación de hasta el
          100% y buscamos adaptarnos a los pedidos y posibilidades de cada cliente,
          brindando siempre una atención clara, cercana y personalizada.
        </p>
      </Reveal>
    </Section>;
}